import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { buildAppMeta, SITE_URL } from "@/lib/seo";
import { useEffect, useMemo, useState } from "react";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/AppShell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ClipboardList, Search, X, Check, Plus, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { SessionClipsCard } from "@/components/sessions/SessionClipsCard";
import { SessionPolicyActions } from "@/components/sessions/SessionPolicyActions";

const TYPE_SINGULAR: Record<ItemType, string> = {
  technique: "technique",
  drill: "drill",
  recommendation: "recommendation",
  resource: "resource",
};

type ItemType = "technique" | "drill" | "recommendation" | "resource";

type Item = {
  id: string;
  title: string;
  body_text: string | null;
  video_url: string | null;
  item_type: ItemType;
  parent_id: string | null;
  position: number;
};

type Client = { id: string; student_name: string; default_rate_cents?: number | null; last_session_at?: string | null };

type Search = { clientId?: string; date?: string; calendarEventId?: string };

type Picked = { item: Item; parent?: Item | null };

const TYPE_LABEL: Record<ItemType, string> = {
  technique: "Techniques",
  drill: "Drills",
  recommendation: "Recommendations",
  resource: "Resources",
};

export const Route = createFileRoute("/app/log")({
  head: () => ({
    meta: buildAppMeta({
      title: "Log a session — CoachQuick",
      description: "Log a coaching session: capture notes, drills, and generate an automated recap email for your client.",
      path: "/app/log",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/app/log` }],
  }),
  validateSearch: (s: Record<string, unknown>): Search => ({
    clientId: (s.clientId as string) || undefined,
    date: (s.date as string) || undefined,
    calendarEventId: (s.calendarEventId as string) || undefined,
  }),
  component: () => (
    <RequireAuth role="coach">
      <Logger />
    </RequireAuth>
  ),
});

function Logger() {
  const { user } = useAuth();
  const nav = useNavigate();
  const search = Route.useSearch();
  const [clients, setClients] = useState<Client[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  // Ordered attendee list — index 0 is the primary attendee (mirrored to sessions.coach_student_id).
  const [attendeeIds, setAttendeeIds] = useState<string[]>(search.clientId ? [search.clientId] : []);
  const clientId = attendeeIds[0] ?? "";
  const [clientQuery, setClientQuery] = useState("");
  const [picked, setPicked] = useState<Record<string, Picked>>({});
  const [itemQuery, setItemQuery] = useState("");
  const [tab, setTab] = useState<ItemType>("technique");
  const [nextTime, setNextTime] = useState("");
  const [busy, setBusy] = useState(false);
  const [draftSessionId, setDraftSessionId] = useState<string>("");
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [qaTitle, setQaTitle] = useState("");
  const [qaBody, setQaBody] = useState("");
  const [qaVideo, setQaVideo] = useState("");
  const [qaBusy, setQaBusy] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [addingChildFor, setAddingChildFor] = useState<string | null>(null);
  const [newChildTitle, setNewChildTitle] = useState("");
  const [savingChild, setSavingChild] = useState(false);

  // When a primary attendee is picked, ensure there's a draft session row so clips can attach to it.
  // When primary changes (host swap), update sessions.coach_student_id.
  useEffect(() => {
    if (!user || !clientId) {
      setDraftSessionId("");
      return;
    }
    let cancelled = false;
    (async () => {
      if (draftSessionId) {
        // Primary swapped — update existing draft row.
        const { error } = await supabase
          .from("sessions")
          .update({ coach_student_id: clientId } as never)
          .eq("id", draftSessionId);
        if (!cancelled && error) toast.error(error.message);
        return;
      }
      const insertPayload: Record<string, unknown> = {
        coach_id: user.id,
        coach_student_id: clientId,
        status: "draft",
      };
      if (search.date) insertPayload.session_date = search.date;
      const { data, error } = await supabase
        .from("sessions")
        .insert(insertPayload as never)
        .select("id")
        .single();
      if (cancelled) return;
      if (error) {
        toast.error(error.message);
        return;
      }
      const newId = (data as any).id as string;
      setDraftSessionId(newId);
      // If this draft was started from a calendar event, link it.
      if (search.calendarEventId) {
        await supabase
          .from("coach_calendar_event_links" as never)
          .upsert(
            {
              coach_id: user.id,
              google_event_id: search.calendarEventId,
              client_id: clientId,
              session_id: newId,
              dismissed: false,
            } as never,
            { onConflict: "coach_id,google_event_id" } as never,
          );
      }
    })();
    return () => {
      cancelled = true;
    };
    // draftSessionId intentionally omitted to avoid loop after initial create.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, clientId, search.date, search.calendarEventId]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const [{ data: cs }, { data: ds }, { data: recent }] = await Promise.all([
        supabase
          .from("coach_students")
          .select("id, student_name, default_rate_cents")
          .eq("coach_id", user.id)
          .order("student_name"),
        supabase
          .from("drills")
          .select("id, title, body_text, video_url, item_type, parent_id, position")
          .eq("coach_id", user.id)
          .order("position")
          .order("title"),
        supabase
          .from("sessions")
          .select("coach_student_id, created_at")
          .eq("coach_id", user.id)
          .gte("created_at", since)
          .order("created_at", { ascending: false }),
      ]);

      const recentMap = new Map<string, string>();
      for (const r of (recent as any[]) ?? []) {
        if (r.coach_student_id && !recentMap.has(r.coach_student_id)) {
          recentMap.set(r.coach_student_id, r.created_at);
        }
      }

      const enriched = ((cs as Client[]) ?? []).map((c) => ({
        ...c,
        last_session_at: recentMap.get(c.id) ?? null,
      }));
      setClients(enriched);
      setItems((ds as Item[]) ?? []);
    })();
  }, [user]);

  const recents = useMemo(
    () =>
      clients
        .filter((c) => c.last_session_at)
        .sort((a, b) => (b.last_session_at! > a.last_session_at! ? 1 : -1))
        .slice(0, 6),
    [clients]
  );

  const otherClients = useMemo(() => {
    const recentIds = new Set(recents.map((r) => r.id));
    const q = clientQuery.toLowerCase().trim();
    return clients
      .filter((c) => !recentIds.has(c.id))
      .filter((c) => !q || c.student_name.toLowerCase().includes(q));
  }, [clients, recents, clientQuery]);

  const itemsByParent = useMemo(() => {
    const m = new Map<string, Item[]>();
    for (const it of items) {
      if (it.parent_id) {
        const arr = m.get(it.parent_id) ?? [];
        arr.push(it);
        m.set(it.parent_id, arr);
      }
    }
    return m;
  }, [items]);

  const itemById = useMemo(() => {
    const m = new Map<string, Item>();
    for (const it of items) m.set(it.id, it);
    return m;
  }, [items]);

  const filteredParents = useMemo(() => {
    const q = itemQuery.toLowerCase().trim();
    const matches = (it: Item) =>
      !q ||
      it.title.toLowerCase().includes(q) ||
      (it.body_text ?? "").toLowerCase().includes(q);
    return items
      .filter((it) => !it.parent_id && it.item_type === tab)
      .filter((p) => matches(p) || (itemsByParent.get(p.id) ?? []).some(matches));
  }, [items, tab, itemQuery, itemsByParent]);

  function toggleAttendee(id: string) {
    setAttendeeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function togglePick(it: Item) {
    setPicked((p) => {
      const next = { ...p };
      if (next[it.id]) delete next[it.id];
      else next[it.id] = { item: it, parent: it.parent_id ? itemById.get(it.parent_id) ?? null : null };
      return next;
    });
  }

  // Auto-expand parents whose children match the search query.
  useEffect(() => {
    const q = itemQuery.toLowerCase().trim();
    if (!q) return;
    setExpanded((prev) => {
      const next = { ...prev };
      for (const parent of items) {
        if (parent.parent_id) continue;
        const kids = itemsByParent.get(parent.id) ?? [];
        const parentMatches =
          parent.title.toLowerCase().includes(q) ||
          (parent.body_text ?? "").toLowerCase().includes(q);
        const childMatches = kids.some(
          (c) => c.title.toLowerCase().includes(q) || (c.body_text ?? "").toLowerCase().includes(q),
        );
        if (childMatches && !parentMatches) next[parent.id] = true;
      }
      return next;
    });
  }, [itemQuery, items, itemsByParent]);

  async function addChild(parent: Item) {
    if (!user) return;
    const title = newChildTitle.trim();
    if (!title) {
      toast.error("Title is required");
      return;
    }
    setSavingChild(true);
    try {
      const kids = itemsByParent.get(parent.id) ?? [];
      const nextPos = kids.reduce((m, k) => Math.max(m, k.position ?? 0), 0) + 1;
      const payload = {
        coach_id: user.id,
        item_type: parent.item_type,
        parent_id: parent.id,
        position: nextPos,
        title,
        body_text: null,
        description: null,
        video_url: null,
        tags: [],
      };
      const { data, error } = await supabase
        .from("drills")
        .insert(payload as never)
        .select("id, title, body_text, video_url, item_type, parent_id, position")
        .single();
      if (error) throw error;
      const newItem = data as Item;
      setItems((prev) => [...prev, newItem]);
      togglePick(newItem);
      setNewChildTitle("");
      setAddingChildFor(null);
      setExpanded((e) => ({ ...e, [parent.id]: true }));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to add");
    } finally {
      setSavingChild(false);
    }
  }

  function openQuickAdd() {
    setQaTitle(itemQuery.trim());
    setQaBody("");
    setQaVideo("");
    setQuickAddOpen(true);
  }

  async function quickAddSave(e: React.FormEvent) {
    e.preventDefault();
    if (!user) return;
    if (!qaTitle.trim()) {
      toast.error("Title is required");
      return;
    }
    setQaBusy(true);
    try {
      const payload = {
        coach_id: user.id,
        item_type: tab,
        title: qaTitle.trim(),
        body_text: qaBody.trim() || null,
        description: qaBody.trim() || null,
        video_url: qaVideo.trim() || null,
        tags: [],
      };
      const { data, error } = await supabase
        .from("drills")
        .insert(payload as never)
        .select("id, title, body_text, video_url, item_type, parent_id, position")
        .single();
      if (error) throw error;
      const newItem = data as Item;
      setItems((prev) => [...prev, newItem]);
      togglePick(newItem);
      setQuickAddOpen(false);
      setItemQuery("");
      toast.success(`Added "${newItem.title}" to library`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to add");
    } finally {
      setQaBusy(false);
    }
  }

  async function save() {
    if (!user || !clientId) {
      toast.error("Pick a client first");
      return;
    }
    if (!draftSessionId) {
      toast.error("Still preparing session — try again in a moment");
      return;
    }
    setBusy(true);
    try {
      const cs = clients.find((c) => c.id === clientId);
      const sessionId = draftSessionId;

      const { error: e1 } = await supabase
        .from("sessions")
        .update({
          notes: nextTime || null,
        } as never)
        .eq("id", sessionId);
      if (e1) throw e1;

      const rows = Object.values(picked).map(({ item }) => ({
        session_id: sessionId,
        drill_id: item.id,
        completed: true,
        is_homework: item.item_type === "drill",
      }));
      if (rows.length > 0) {
        const { error: e2 } = await supabase.from("session_drills").insert(rows as never);
        if (e2) throw e2;
      }

      // Sync session_attendees to match the picked attendee list.
      // Delete-then-insert keeps this idempotent for repeated saves of the same draft.
      const { error: eDel } = await supabase
        .from("session_attendees")
        .delete()
        .eq("session_id", sessionId);
      if (eDel) throw eDel;

      const attendeeRows = attendeeIds.map((cid) => {
        const c = clients.find((x) => x.id === cid);
        return {
          session_id: sessionId,
          coach_student_id: cid,
          attended: true,
          billed_amount_cents: c?.default_rate_cents ?? null,
        };
      });
      if (attendeeRows.length > 0) {
        const { error: eA } = await supabase
          .from("session_attendees")
          .insert(attendeeRows as never);
        if (eA) throw eA;
      }

      const groupLabel =
        attendeeIds.length > 1
          ? `${cs?.student_name ?? "client"} + ${attendeeIds.length - 1} other${attendeeIds.length - 1 === 1 ? "" : "s"}`
          : cs?.student_name ?? "client";
      toast.success(`Session logged for ${groupLabel}`);
      nav({ to: "/app/sessions/$sessionId", params: { sessionId } });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  const noClients = clients.length === 0;
  const noItems = items.length === 0;
  const pickedList = Object.values(picked);

  return (
    <AppShell>
      <div>
        <h1 className="text-2xl font-bold tracking-tight md:text-3xl">Log session</h1>
        <p className="text-sm text-muted-foreground">Pick a client, tap what you covered, save.</p>
      </div>

      {(noClients || noItems) && (
        <Card className="mt-6 border-primary/30 bg-primary/5 p-5">
          <p className="text-sm">
            {noClients && "Add a client first. "}
            {noItems && "Your library is empty — seed the starter library or add items first. "}
          </p>
        </Card>
      )}

      {/* STEP 1 — Client(s) */}
      <Card className="mt-6 p-5">
        <div className="flex items-baseline justify-between">
          <Label className="text-sm font-semibold">
            1. Client{attendeeIds.length > 1 ? "s" : ""}
            {attendeeIds.length > 1 && (
              <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-brand-text">
                Group · {attendeeIds.length}
              </span>
            )}
          </Label>
          {attendeeIds.length > 0 && (
            <button
              type="button"
              onClick={() => setAttendeeIds([])}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Clear
            </button>
          )}
        </div>

        {attendeeIds.length > 0 && (
          <p className="mt-3 text-xs text-muted-foreground">
            Tap more clients to add them as attendees. The first attendee is the primary (used for invoicing & nudges).
          </p>
        )}

        {recents.length > 0 && (
          <>
            <p className="mt-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">Recent</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {recents.map((c) => (
                <ClientButton
                  key={c.id}
                  client={c}
                  active={attendeeIds.includes(c.id)}
                  primary={attendeeIds[0] === c.id && attendeeIds.length > 1}
                  onClick={() => toggleAttendee(c.id)}
                />
              ))}
            </div>
          </>
        )}

        <div className="relative mt-4">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={clientQuery}
            onChange={(e) => setClientQuery(e.target.value)}
            placeholder="Search clients…"
            className="pl-9"
          />
        </div>
        {otherClients.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {otherClients.slice(0, 30).map((c) => (
              <ClientButton
                key={c.id}
                client={c}
                active={attendeeIds.includes(c.id)}
                primary={attendeeIds[0] === c.id && attendeeIds.length > 1}
                onClick={() => toggleAttendee(c.id)}
              />
            ))}
          </div>
        )}

        {user && clientId && draftSessionId && (
          <div className="mt-5 rounded-lg border border-dashed border-border bg-muted/30 p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Wasn't a normal lesson?
            </p>
            <p className="mt-1 mb-3 text-xs text-muted-foreground">
              Skip drills and record a reschedule, cancellation, or no-show instead.
            </p>
            <SessionPolicyActions
              sessionId={draftSessionId}
              coachId={user.id}
              clientId={clientId}
              sessionDate={search.date ?? new Date().toISOString().slice(0, 10)}
              compact
              onConfirmed={async (kind, { charged }) => {
                const sid = draftSessionId;
                setDraftSessionId("");
                // Mark the draft as discarded so it never appears in recap queues —
                // a policy event (reschedule/cancel/no-show) means there was no lesson to recap.
                if (sid) {
                  await supabase
                    .from("sessions")
                    .update({ discarded_at: new Date().toISOString() } as never)
                    .eq("id", sid);
                }
                if (kind === "reschedule") {
                  toast.success("Lesson rescheduled.");
                  nav({ to: "/app" });
                } else if (charged) {
                  toast.success(
                    kind === "no_show"
                      ? "No-show fee ready to bill."
                      : "Cancellation fee ready to bill.",
                  );
                  nav({ to: "/app/invoices" });
                } else {
                  toast.success(kind === "no_show" ? "Marked no-show." : "Lesson cancelled.");
                  nav({ to: "/app" });
                }
              }}
            />
          </div>
        )}
      </Card>

      {/* STEP 2 — Items covered */}
      <Card className="mt-6 p-5">
        <Label className="text-sm font-semibold">2. What did you cover?</Label>

        {pickedList.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {pickedList.map(({ item, parent }) => (
              <span
                key={item.id}
                className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-brand-text"
              >
                <span>
                  {parent ? <span className="opacity-70">{parent.title} › </span> : null}
                  {item.title}
                </span>
                <button onClick={() => togglePick(item)} className="-mr-1 rounded-full p-0.5 hover:bg-primary/20">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="relative mt-4">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={itemQuery}
            onChange={(e) => setItemQuery(e.target.value)}
            placeholder="Search library…"
            className="pl-9"
          />
        </div>

        <Tabs value={tab} onValueChange={(v) => setTab(v as ItemType)} className="mt-4">
          <TabsList className="w-full justify-start overflow-x-auto">
            {(Object.keys(TYPE_LABEL) as ItemType[]).map((t) => (
              <TabsTrigger key={t} value={t}>
                {TYPE_LABEL[t]}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value={tab} className="mt-3">
            <div className="grid gap-2 sm:grid-cols-2">
              <button
                type="button"
                onClick={openQuickAdd}
                className="flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border p-3 text-sm font-medium text-muted-foreground transition-colors hover:border-primary hover:text-brand-text"
              >
                <Plus className="h-4 w-4" />
                {itemQuery.trim()
                  ? `Add "${itemQuery.trim()}" as new ${TYPE_SINGULAR[tab]}`
                  : `Add new ${TYPE_SINGULAR[tab]}`}
              </button>
              {filteredParents.map((parent) => {
                const children = itemsByParent.get(parent.id) ?? [];
                const sel = picked[parent.id];
                const isOpen = expanded[parent.id] ?? false;
                const anyChildPicked = children.some((c) => picked[c.id]);
                const isAdding = addingChildFor === parent.id;
                return (
                  <div
                    key={parent.id}
                    className={`rounded-lg border-2 transition-colors ${
                      sel || anyChildPicked ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40"
                    }`}
                  >
                    <div className="flex items-start gap-1 p-3">
                      <button
                        type="button"
                        aria-label={isOpen ? "Collapse" : "Expand"}
                        onClick={() => setExpanded((e) => ({ ...e, [parent.id]: !isOpen }))}
                        className="mt-0.5 shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                      >
                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => togglePick(parent)}
                        className="flex flex-1 items-start gap-3 text-left"
                      >
                        <span
                          className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 ${
                            sel ? "border-primary bg-primary text-primary-foreground" : "border-border"
                          }`}
                        >
                          {sel && <Check className="h-3 w-3" />}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold leading-tight">{parent.title}</p>
                          {parent.body_text && (
                            <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{parent.body_text}</p>
                          )}
                          <p className="mt-1 text-[11px] text-muted-foreground">
                            {children.length === 0
                              ? "No sub-techniques yet — tap ▸ to add one"
                              : `${children.length} sub-technique${children.length === 1 ? "" : "s"}${anyChildPicked ? " · selected" : ""}`}
                          </p>
                        </div>
                      </button>
                    </div>
                    {isOpen && (
                      <div className="space-y-1 border-t bg-muted/30 px-3 py-2">
                        {children.map((child) => {
                          const csel = picked[child.id];
                          return (
                            <button
                              key={child.id}
                              type="button"
                              onClick={() => togglePick(child)}
                              className="flex w-full items-start gap-2 rounded-md p-1.5 text-left hover:bg-background"
                            >
                              <span
                                className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 ${
                                  csel ? "border-primary bg-primary text-primary-foreground" : "border-border"
                                }`}
                              >
                                {csel && <Check className="h-2.5 w-2.5" />}
                              </span>
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-medium leading-tight">{child.title}</p>
                                {child.body_text && (
                                  <p className="mt-0.5 line-clamp-1 text-[11px] text-muted-foreground">{child.body_text}</p>
                                )}
                              </div>
                            </button>
                          );
                        })}
                        {isAdding ? (
                          <div className="flex items-center gap-2 p-1.5">
                            <Input
                              autoFocus
                              value={newChildTitle}
                              onChange={(e) => setNewChildTitle(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  void addChild(parent);
                                } else if (e.key === "Escape") {
                                  setAddingChildFor(null);
                                  setNewChildTitle("");
                                }
                              }}
                              placeholder="Sub-technique title…"
                              className="h-8 text-xs"
                              disabled={savingChild}
                            />
                            <Button
                              type="button"
                              size="sm"
                              className="h-8"
                              onClick={() => void addChild(parent)}
                              disabled={savingChild || !newChildTitle.trim()}
                            >
                              {savingChild ? "…" : "Add"}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="h-8"
                              onClick={() => {
                                setAddingChildFor(null);
                                setNewChildTitle("");
                              }}
                              disabled={savingChild}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              setAddingChildFor(parent.id);
                              setNewChildTitle("");
                            }}
                            className="flex w-full items-center gap-2 rounded-md p-1.5 text-left text-xs font-medium text-muted-foreground hover:bg-background hover:text-brand-text"
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Add sub-technique
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {filteredParents.length === 0 && (
              <p className="mt-3 text-center text-xs text-muted-foreground">
                Nothing matches in your library yet — use "Add new" above.
              </p>
            )}
          </TabsContent>
        </Tabs>

        {/* Quick-add dialog */}
        <Dialog open={quickAddOpen} onOpenChange={setQuickAddOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add new {TYPE_SINGULAR[tab]}</DialogTitle>
            </DialogHeader>
            <form onSubmit={quickAddSave} className="space-y-4">
              <div>
                <Label htmlFor="qa-title">Title</Label>
                <Input
                  id="qa-title"
                  value={qaTitle}
                  onChange={(e) => setQaTitle(e.target.value)}
                  required
                  autoFocus
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="qa-body">Description (optional, used in recap email)</Label>
                <Textarea
                  id="qa-body"
                  rows={3}
                  value={qaBody}
                  onChange={(e) => setQaBody(e.target.value)}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="qa-video">Link / Video URL (optional)</Label>
                <Input
                  id="qa-video"
                  value={qaVideo}
                  onChange={(e) => setQaVideo(e.target.value)}
                  placeholder="https://…"
                  className="mt-1.5"
                />
              </div>
              <Button type="submit" className="w-full" disabled={qaBusy}>
                {qaBusy ? "Adding…" : "Add & select"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </Card>

      {/* STEP 2.5 — Clips (only after a client is picked + draft exists) */}
      {clientId && draftSessionId && user && (
        <SessionClipsCard sessionId={draftSessionId} coachId={user.id} />
      )}

      {/* STEP 3 — Next time */}
      <Card className="mt-6 p-5">
        <Label htmlFor="nt" className="text-sm font-semibold">3. Anything for next time?</Label>
        <p className="mt-1 text-xs text-muted-foreground">
          Becomes the forward-looking line in the email — e.g. "Expect more footwork next lesson, plus drives."
        </p>
        <Textarea
          id="nt"
          value={nextTime}
          onChange={(e) => setNextTime(e.target.value)}
          rows={3}
          className="mt-3"
          placeholder="Optional…"
        />
      </Card>

      <div className="sticky bottom-4 mt-6">
        <Button size="lg" className="w-full text-base" disabled={busy || !clientId} onClick={save}>
          <ClipboardList className="h-5 w-5" /> {busy ? "Saving…" : "Save & draft email"}
        </Button>
      </div>
    </AppShell>
  );
}

function ClientButton({
  client,
  active,
  primary,
  onClick,
}: {
  client: Client;
  active: boolean;
  primary?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative rounded-lg border-2 px-4 py-3 text-sm font-semibold transition-colors ${
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-card hover:border-primary/40"
      }`}
    >
      {client.student_name}
      {primary && (
        <span className="ml-2 rounded-full bg-primary-foreground/20 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide">
          Primary
        </span>
      )}
    </button>
  );
}
